import config from '../config';
import analytics from '../listing/analytics';
import { SortByPropMap, SortOrderEnum } from '../listing/typings';
import CharacterUtils from '../listing/utils/CharacterUtils';
import byProp from '../listing/utils/byProp';
import ApiUtils from './ApiUtils';
import UserApi from './UserApi';

const mockFetchSuccess = (data) =>
    new Promise((resolve) =>
        resolve({
            json: () => new Promise((resolve2) => resolve2(data)),
        }),
    );

const throwIfNotOk = (response) => {
    if (!response.ok) {
        throw new Error('Non-OK response from GET characters');
    }

    return response;
};

const getCharacters = () =>
    UserApi.getUserId()
        .then((userId) => ApiUtils.get(`${config.characterServiceBaseUrl}/characters/list?userId=${userId}`))
        .then(throwIfNotOk);

const queryListToMap = (queries) =>
    queries.reduce((queryMap, { key, value }) => {
        queryMap[key] = value;

        return queryMap;
    }, {});

const matchesSearchTerm = (lowerSearch) => (searchableTerm) => searchableTerm.toLowerCase().includes(lowerSearch);

const applyQueriesClientSide = (queryMap) => (json) => {
    let data = json.data || [];

    const { search, sortBy, sortOrder = SortOrderEnum.Ascending } = queryMap;

    if (search) {
        const matchesSearch = matchesSearchTerm(search.toLowerCase());
        data = data.filter((character) => CharacterUtils.getSearchableTerms(character).some(matchesSearch));

        // Logging this here will duplicate the log if a search term is used and then the sort is changed.
        // If this was logged in MyCharactersListing.handleSearchChanged, then it wouldn't have the context of all data and would miss matches in some scenarios.
        // This function is debounced to prevent spamming the logs as the user types.
        analytics.logListingSearchChanged(data, matchesSearch);
    }

    const sortByPropSelector = SortByPropMap[sortBy];

    if (sortByPropSelector) {
        data = data.sort(byProp(sortByPropSelector));

        if (sortOrder === SortOrderEnum.Descending) {
            data.reverse();
        }
    }

    return {
        ...json,
        data,
    };
};

const getItems = ({ queries }) =>
    getCharacters()
        .then(ApiUtils.getJsonFromResponse)
        .then(applyQueriesClientSide(queryListToMap(queries)))
        .then(mockFetchSuccess);

const getAllCharacters = () => getCharacters().then(ApiUtils.getJsonFromResponse);

const copyCharacter = (character) =>
    ApiUtils.post(`${config.characterServiceBaseUrl}/character/copy`, {
        characterId: CharacterUtils.getId(character),
    });

const deleteCharacter = (character) =>
    ApiUtils.del(`${config.characterServiceBaseUrl}/character`, {
        characterId: CharacterUtils.getId(character),
    });

const leaveCampaign = (character) =>
    ApiUtils.del(
        `${config.ddbBaseUrl}/api/campaign/${CharacterUtils.getCampaignId(character)}/character/${CharacterUtils.getId(
            character,
        )}`,
    );

const unlockCharacters = (characterIds) =>
    ApiUtils.post(`${config.userServiceBaseUrl}/unlock`, {
        characterIds,
    });

const activateCharacter = (character) =>
    ApiUtils.put(`${config.characterServiceBaseUrl}/characters/${CharacterUtils.getId(character)}/status/activate`);

const getFeatureFlagsFromCharacterService = (flags) =>
    ApiUtils.post(`${config.characterServiceBaseUrl}/featureflag`, { flags });

export default {
    getAllCharacters,
    copyCharacter,
    deleteCharacter,
    getItems,
    leaveCampaign,
    unlockCharacters,
    activateCharacter,
    getFeatureFlagsFromCharacterService,
};
